import { useCallback, useState } from 'react';
import { SelectedFile } from '../../../../types/file';
import { validateImageFile } from '../../../../util/update-image';
import { useSnackbar } from 'notistack';
import { useHistory } from 'react-router-dom';
import {
  CouponPackMaster,
  Extension,
  ImageContainerInput,
  ImageType,
  UpdateCouponPackMasterInput,
  useCouponMasterUploadImageUrlMutation,
  useUpdateCouponPackMasterMutation,
} from '../../../../graphql/generated';
import axios from 'axios';
import Bugsnag from '@bugsnag/js';

export const MB_1 = 1000000;

export type UploadImageProps = {
  loading: boolean;
  validateAndSetLogoImage: (file: File) => void;
  setDeleteLogoImage: () => void;
  validateAndSetCouponImage: (file: File) => void;
  setDeleteCouponImage: () => void;

  selectedFiles: {
    logoImage: SelectedFile;
    couponImage: SelectedFile;
  };

  validateImageFiles: () => boolean;
  shouldUploadImage: (couponPackMasterId: string) => boolean;
  uploadImages: (couponPackMaster: CouponPackMaster) => Promise<void>;
};

type UploadImage = { type: string; key: string };

export const useUploadImage = (): UploadImageProps => {
  const [loading, setLoading] = useState(false);
  const history = useHistory();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const [imageFileMutation] = useCouponMasterUploadImageUrlMutation();
  const [updateCouponPackMaster] = useUpdateCouponPackMasterMutation();

  const getRequiredPrevious = (m: CouponPackMaster): UpdateCouponPackMasterInput => {
    return {
      available: m.available,
      couponCode: m.couponCode,
      couponName: m.couponName,
      displayCouponName: m.displayCouponName,

      displayStartDate: m.displayStartDate,
      displayEndDate: m.displayEndDate,

      validityStartDateTime: m.validityStartDateTime,
      validityEndDateTime: m.validityEndDateTime,
      description: m.description,
      note: m.note,
      ...(m.status !== 'ISSUED'
        ? {
            products: m.products.map((p) => ({
              quantity: Number(p.quantity),
              couponMasterId: p.couponMasterId,
              couponMasterName: p.couponMasterName,
              maxCouponsPerOrder: p.maxCouponsPerOrder,
            })),
          }
        : {}),
      logoImage: { key: '' },
      couponImage: { key: '' },
    };
  };

  const upload = useCallback(
    async (input: { mutationResult: CouponPackMaster; files: { file: SelectedFile; type: ImageType }[] }) => {
      setLoading(true);

      const m = input.mutationResult;
      const requiredPrevious: UpdateCouponPackMasterInput = getRequiredPrevious(m);

      try {
        const uploadImages: UploadImage[] = [];
        for (let i = 0; i < input.files.length; i++) {
          if (input.files[i].file.deleteFlag) {
            uploadImages.push({
              type: input.files[i].type,
              key: '',
            });
            continue;
          }

          const imageUrl = await imageFileMutation({
            variables: {
              input: {
                couponPackMasterId: m.id,
                extension: input.files[i].file.ext.message as Extension,
                imageType: input.files[i].type,
              },
            },
          });

          if (!imageUrl.data || !input.files[i].file || !input.files[i].file.file) {
            return Promise.reject(JSON.stringify(imageUrl.errors));
          }

          let contentType = input.files[i].file.file?.type;

          if (!contentType) {
            const extension = input.files[i].file.ext.message as Extension;
            const imageExtension = extension === 'jpg' || extension === 'jpeg' ? 'jpeg' : extension;
            contentType = `image/${imageExtension}`;
          }

          await axios.put(imageUrl.data.couponMasterUploadImageUrl.preSignedUrl, input.files[i].file.file, {
            headers: {
              'Content-Type': contentType,
            },
          });

          uploadImages.push({
            type: input.files[i].type,
            key: imageUrl.data.couponMasterUploadImageUrl.key,
          });
        }

        if (uploadImages.length > 0) {
          const logoImage: UploadImage | undefined = uploadImages.find((u) => u.type === 'product');
          const couponImage: UploadImage | undefined = uploadImages.find((u) => u.type === 'company');

          const logoImageInput: ImageContainerInput | undefined = logoImage ? { key: logoImage.key } : undefined;
          const couponImageInput: ImageContainerInput | undefined = couponImage ? { key: couponImage.key } : undefined;

          await updateCouponPackMaster({
            variables: {
              id: m.id,
              input: {
                ...requiredPrevious,
                logoImage: logoImageInput,
                couponImage: couponImageInput,
              },
            },
          });
        }
      } catch (err) {
        Bugsnag.notify(err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [imageFileMutation]
  );

  const initialSelectedFile: SelectedFile = {
    ext: {
      message: '未選択',
      valid: undefined,
    },
    length: {
      message: '未選択',
      valid: undefined,
    },
    toUpdate: false,
  };

  const deleteSelectedFile: SelectedFile = {
    deleteFlag: true,
    ext: {
      message: '削除されます',
      valid: undefined,
    },
    length: {
      message: '削除されます',
      valid: undefined,
    },
    file: undefined,
    toUpdate: true,
  };

  const [selectedLogoImage, setSelectedLogoImage] = useState<SelectedFile>(initialSelectedFile);
  const [selectedCouponImage, setSelectedCouponImage] = useState<SelectedFile>(initialSelectedFile);

  const validateAndSetLogoImage = useCallback(
    (file: File) => validateImageFile(file, MB_1, setSelectedLogoImage),
    [setSelectedLogoImage]
  );
  const validateAndSetCouponImage = useCallback(
    (file: File) => validateImageFile(file, MB_1, setSelectedCouponImage),
    [setSelectedCouponImage]
  );

  const setDeleteLogoImage = useCallback(() => setSelectedLogoImage(deleteSelectedFile), [setSelectedLogoImage]);
  const setDeleteCouponImage = useCallback(() => setSelectedCouponImage(deleteSelectedFile), [setSelectedCouponImage]);

  const validateImageFiles = (): boolean => {
    const invalidLogoImage =
      selectedLogoImage.file && (!selectedLogoImage.length.valid || !selectedLogoImage.ext.valid);
    const invalidCouponImage =
      selectedCouponImage.file && (!selectedCouponImage.length.valid || !selectedCouponImage.ext.valid);

    if (invalidLogoImage || invalidCouponImage) {
      const key = enqueueSnackbar('アップロード画像を確認してください', {
        variant: 'warning',
        persist: true,
        onClick: () => closeSnackbar(key),
      });
      return true;
    }

    return false;
  };

  const shouldUploadImage = (couponPackMasterId: string): boolean => {
    if (!selectedLogoImage.toUpdate && !selectedCouponImage.toUpdate) {
      enqueueSnackbar(`回数券マスタデータを登録しました`, {
        variant: 'success',
      });
      history.push({
        pathname: `/coupon-pack-masters/${couponPackMasterId}`,
        search: history.location.search,
      });
      return false;
    }

    return true;
  };

  const uploadImages = async (couponPackMaster: CouponPackMaster): Promise<void> => {
    const uploadFiles: { file: SelectedFile; type: ImageType }[] = [];

    if (selectedLogoImage.toUpdate) {
      uploadFiles.push({
        file: selectedLogoImage,
        type: 'product',
      });
    }

    if (selectedCouponImage.toUpdate) {
      uploadFiles.push({
        file: selectedCouponImage,
        type: 'company',
      });
    }

    await upload({
      mutationResult: couponPackMaster,
      files: uploadFiles,
    });
  };

  return {
    loading,
    validateAndSetLogoImage,
    setDeleteLogoImage,
    validateAndSetCouponImage,
    setDeleteCouponImage,
    selectedFiles: {
      logoImage: selectedLogoImage,
      couponImage: selectedCouponImage,
    },
    validateImageFiles,
    shouldUploadImage,
    uploadImages,
  };
};
